import { useEffect, useState, useRef } from 'react';
import config from '../config';

/**
 * 시나리오 챗봇 예약 조회 훅
 *
 * BugHerd #66: 예약번호(VVT-...) 식별성이 떨어진다는 보고로 이메일 기반 조회로 전환.
 * 입력값이 '@' 포함이면 GET /api/reservations/lookup?email={input} (목록 → 최근 1건),
 * 그 외는 기존 GET /api/reservations/{id} (예약번호 단건). 양쪽 모두 호환 유지.
 *
 * 사용법:
 *   const { data, error, loading, hoursUntilDeparture } = useReservationLookup(input);
 *
 * 반환:
 *   data    : 예약 정보 객체 (또는 null)
 *   error   : 에러 객체 (또는 null)
 *   loading : 로딩 중 여부
 *   hoursUntilDeparture : 출발까지 남은 시간 (시간 단위, 음수면 출발 후)
 */
export default function useReservationLookup(reservationId, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!enabled || !reservationId) {
      setData(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const isEmail = String(reservationId).includes('@');
    const url = isEmail
      ? `${config.API_URL}/api/reservations/lookup?email=${encodeURIComponent(reservationId)}`
      : `${config.API_URL}/api/reservations/${reservationId}`;

    fetch(url, {
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((json) => {
        if (!controller.signal.aborted) {
          // lookup 응답은 배열, 단건은 객체 — 배열이면 최신순 첫 번째 사용 (BE 가 created_at desc 정렬).
          const picked = Array.isArray(json) ? (json[0] || null) : json;
          setData(picked);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err);
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [reservationId, enabled]);

  // 취소 분기용 — 출발까지 남은 시간 계산
  // #196: BE 응답 형식이 route.reservation_date + route.departure.time(nested) 인데
  // 기존엔 flat 필드(departure_at/start_at/datetime)만 봤음 → 항상 null → 챗봇 메시지
  // "출발까지 [X]시간 남았습니다" 의 {{hours}} 가 빈 채로 렌더("출발까지 시간 남았습니다")
  // 되던 회귀. nested 형식 우선, 폴백으로 flat 형식도 유지.
  let hoursUntilDeparture = null;
  const dateStr = data?.route?.reservation_date || null;
  const timeStr = (data?.route?.departure?.time || '').toString().slice(0, 5);
  let departureMs = null;
  if (dateStr && timeStr) {
    const ts = new Date(`${dateStr}T${timeStr}:00+09:00`).getTime();
    if (!Number.isNaN(ts)) departureMs = ts;
  }
  if (departureMs === null) {
    const flatTs = data?.departure_at || data?.start_at || data?.datetime;
    if (flatTs) {
      const ts = new Date(flatTs).getTime();
      if (!Number.isNaN(ts)) departureMs = ts;
    }
  }
  if (departureMs !== null) {
    hoursUntilDeparture = (departureMs - Date.now()) / (1000 * 60 * 60);
  }

  return { data, error, loading, hoursUntilDeparture };
}
